// Sidebar — the map of the panel.
//
// Every entry, its group and its one-line purpose come from
// constants/navigation.js, and the live counts come from the shared work list.
// A number next to an item means "there is work on this screen right now", so
// the admin never has to open pages to find out where the work is.

import React, { useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { capcrmlogo } from '../../assets';
import { FiX, FiArrowRight, FiDollarSign, FiKey } from 'react-icons/fi';
import { NAV_SECTIONS } from '../../constants/navigation';
import { ROUTES } from '../../constants/app.constants';
import { useWorklist } from '../../hooks/useWorklist';

// Every page mounts its own <MainLayout>, so navigating throws this component
// away and builds a fresh one — a brand new scroll container that starts at the
// top, yanking the item you just clicked out of view. Parking the position in
// module scope lets it survive the remount.
let savedNavScroll = 0;

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { countFor } = useWorklist();
  const navRef = useRef(null);

  const isActive = (path) =>
    location.pathname === path
    || (path !== '/' && location.pathname.startsWith(`${path}/`));

  // Before the browser paints, so the menu never visibly jumps.
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    if (savedNavScroll > 0) {
      nav.scrollTop = savedNavScroll;
      return;
    }

    // Nothing saved yet — a fresh login or a reload straight onto a deep link.
    // Pull the active item into view instead of assuming it's near the top.
    const active = nav.querySelector('[data-active="true"]');
    if (!active) return;
    const itemBox = active.getBoundingClientRect();
    const navBox = nav.getBoundingClientRect();
    if (itemBox.top < navBox.top || itemBox.bottom > navBox.bottom) {
      nav.scrollTop += itemBox.top - navBox.top - (navBox.height - itemBox.height) / 2;
      savedNavScroll = nav.scrollTop;
    }
  }, []);

  return (
    <aside
      className={`
        fixed inset-y-0 left-0 z-50 w-72
        lg:static lg:z-auto lg:w-64
        bg-slate-900 flex flex-col
        transition-transform duration-300 ease-in-out shrink-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}
    >
      {/* Brand */}
      <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-700/60 shrink-0">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0">
          <img src={capcrmlogo} alt="CAP" className="w-full h-full object-contain" />
        </div>
        <div className="leading-tight flex-1 min-w-0">
          <p className="text-white font-bold text-sm tracking-wide">CAP Admin</p>
          <p className="text-slate-400 text-xs">Coimbatore Auto Parts</p>
        </div>
        <button
          onClick={onClose}
          className="lg:hidden w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          aria-label="Close menu"
        >
          <FiX className="w-4 h-4" />
        </button>
      </div>

      {/* Navigation */}
      <nav
        ref={navRef}
        onScroll={(e) => { savedNavScroll = e.currentTarget.scrollTop; }}
        className="flex-1 px-3 py-4 space-y-5 overflow-y-auto"
      >
        {NAV_SECTIONS.map((group) => (
          <div key={group.id}>
            <p className="text-slate-500 text-[10px] font-bold tracking-widest px-3">
              {group.label}
            </p>
            {group.caption && (
              <p className="text-slate-600 text-[10px] font-medium px-3 mt-0.5">{group.caption}</p>
            )}
            <div className="space-y-0.5 mt-1.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const waiting = item.badge ? countFor(item.badge) : 0;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onClose}
                    title={item.purpose}
                    data-active={active}
                    aria-current={active ? 'page' : undefined}
                    className={`flex items-center gap-2.5 pl-3 pr-2 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      active
                        ? 'bg-violet-600 text-white shadow-lg shadow-violet-900/40'
                        : 'text-slate-400 hover:text-white hover:bg-slate-800'
                    }`}
                  >
                    {/* Order-flow items carry their step number, so the
                        sequence of the day's work reads straight off the menu */}
                    {item.step && (
                      <span className={`shrink-0 w-3 text-[10px] font-black leading-none ${
                        active ? 'text-white/60' : 'text-slate-600'
                      }`}>
                        {item.step}
                      </span>
                    )}
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                    {waiting > 0 && (
                      <span
                        title={`${waiting} waiting`}
                        className={`shrink-0 min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-black flex items-center justify-center ${
                          active ? 'bg-white text-violet-700' : 'bg-amber-500 text-slate-900'
                        }`}
                      >
                        {waiting > 99 ? '99+' : waiting}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* The other workspaces — spelled out, because each needs its own login */}
      <div className="px-3 pb-3 shrink-0 border-t border-slate-700/60 pt-3 space-y-0.5">
        <Link
          to="/expenses"
          onClick={onClose}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
        >
          <FiDollarSign className="w-4 h-4 shrink-0" />
          <span className="flex-1 leading-tight">
            Daily Ops
            <span className="block text-[10px] text-slate-500 font-normal">
              counter sales &amp; expenses — separate login
            </span>
          </span>
          <FiArrowRight className="w-3.5 h-3.5" />
        </Link>
        <Link
          to={ROUTES.CARS_WORK}
          onClick={onClose}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
        >
          <FiKey className="w-4 h-4 shrink-0" />
          <span className="flex-1 leading-tight">
            The Lot
            <span className="block text-[10px] text-slate-500 font-normal">
              what the car team sees — separate login
            </span>
          </span>
          <FiArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      <div className="px-5 py-3 border-t border-slate-700/60 shrink-0">
        <p className="text-slate-500 text-xs text-center">
          v1.0.0 &bull; CAP &copy; {new Date().getFullYear()}
        </p>
      </div>
    </aside>
  );
};

export default Sidebar;
